import {
  Component,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { AccountsSearchComponent } from '../../components/accounts-search/accounts-search.component';
import { DatePickerModule } from 'primeng/datepicker';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AccountContract } from '../../store/accounts/types';
import { TranslocoModule } from '@jsverse/transloco';
import { getToday } from '../../utils/get-today';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';

@Component({
  selector: 'blindspot-plan-visit-popup',
  styleUrls: ['./plan-visit.popup.scss'],
  imports: [
    TextareaModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    AccountsSearchComponent,
    DatePickerModule,
    IconFieldModule,
    InputIconModule,
    TranslocoModule,
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [style]="{ width: '100%', maxWidth: '600px' }"
      [header]="'buttons.planVisit' | transloco"
      #dialog
      (onHide)="onClear()"
      (onShow)="initPopup()"
    >
      <div class="content" *transloco="let t">
        <blindspot-accounts-search
          #search
          class="search"
          [limitToSalesRepScope]="true"
          [showClear]="true"
          [prefillAccount]="prefillAccount()"
          (selectAccount)="selectedAccount.set($event)"
          (clear)="onClear()"
        />
        @let account = selectedAccount();
        @if (account) {
          <div class="inputs">
            <div class="input">
              <label for="date">{{ t('days.date') }}</label>
              <p-datepicker
                [(ngModel)]="date"
                [showIcon]="true"
                dateFormat="DD, MM d"
                appendTo="body"
                iconDisplay="input"
                inputId="date"
                [disabledDays]="[0, 6]"
                [firstDayOfWeek]="1"
              />
            </div>
            <div class="input">
              <label for="time">{{ t('days.time') }}</label>
              <p-datepicker
                [(ngModel)]="time"
                [showIcon]="true"
                [timeOnly]="true"
                [stepMinute]="15"
                hourFormat="24"
                appendTo="body"
                iconDisplay="input"
                inputId="time"
                ><ng-template #inputicon>
                  <i class="pi pi-clock"></i> </ng-template
              ></p-datepicker>
            </div>
          </div>
          <div class="button">
            <p-button
              [label]="'buttons.planVisit' | transloco"
              [loading]="loading()"
              (onClick)="
                planVisit.emit({ account, date: date(), time: time() })
              "
            />
          </div>
        }
      </div>
    </p-dialog>
  `,
})
export class PlanVisitPopup {
  private route = inject(ActivatedRoute);
  private searchComponent =
    viewChild.required<AccountsSearchComponent>('search');

  private urlDate = toSignal(
    this.route.queryParams.pipe(
      map((params) => {
        const d = params['date'] ? new Date(params['date']) : null;
        return d && !isNaN(d.getTime()) ? d : undefined;
      }),
    ),
  );

  public visible = model<boolean>(false);
  public date = signal<Date>(getToday());
  public time = signal<Date>(PlanVisitPopup.roundedNow());
  public loading = input<boolean>(false);
  public prefillAccount = input<AccountContract | undefined>(undefined);
  public accountClear = output<void>();
  public planVisit = output<{
    account: AccountContract;
    date: Date;
    time: Date;
  }>();
  public selectedAccount = signal<AccountContract | undefined>(undefined);
  public today = getToday();

  constructor() {
    effect(() => {
      this.selectedAccount.set(this.prefillAccount());
    });
  }

  private static roundedNow(): Date {
    const d = new Date();
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
    return d;
  }

  public initPopup(): void {
    this.setCurrentTime();
    const prefill = this.prefillAccount();
    this.selectedAccount.set(prefill);
    if (prefill) {
      this.searchComponent().currentSearch.set(prefill.name);
    }
  }

  public setCurrentTime(): void {
    this.date.set(this.urlDate() ?? getToday());
    this.time.set(PlanVisitPopup.roundedNow());
  }

  public onClear() {
    this.date.set(getToday());
    this.time.set(PlanVisitPopup.roundedNow());
    this.selectedAccount.set(undefined);
    this.searchComponent().currentSearch.set('');
  }
}
